import { FirebaseError } from 'firebase/app';
import i18next from 'i18next';

import { trackSentryError } from './analytics/sentry';
import { API_BASE_URL } from './const';
import { getDirectUrl } from './env';
import { Creativity, Item, OutputLanguageData, RequestType, Version } from './types';

export class ReqError extends Error {
  constructor(
    public message: string,
    public status: number
  ) {
    super(message);
    this.name = 'RequestError';
  }
}

export const STATUS_CODES = {
  BAD_REQUEST: 400,
  PAYMENT_REQUIRED: 402,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
  CANT_SCRAPE_WEBSITE: 423,
};

type ApiErrorJSON = {
  data?: { message?: string; error?: string };
  message?: string;
  error?: string;
};

export class ApiError extends Error {
  status: number;

  constructor(
    response: unknown,
    status: number,
    defaultMessage: string = i18next.t('app.errors.unexpectedError')
  ) {
    super(ApiError.extractMessage(response, defaultMessage));
    this.status = status;
    this.name = 'ApiError';
  }

  private static extractMessage(response: unknown, fallback: string): string {
    if (typeof response === 'string') return response || fallback;
    if (typeof response === 'object' && response !== null) {
      const r = response as ApiErrorJSON;
      return r?.data?.message || r?.message || r?.data?.error || r?.error || fallback;
    }
    return fallback;
  }
}

interface ErrorHandlerOptions {
  error: unknown;
  onSetUpgradePopupOpen?: (isOpen: boolean) => void;
  onSetErrorMessage?: (message: string) => void;
  onSetErrorAlertOpen?: (isOpen: boolean) => void;
  onSetErrorButtonText?: (text: string) => void;
  onSetErrorAction?: (action: () => void) => void;
  onRetry?: () => void;
}

export function handleApiError({
  error,
  onSetErrorMessage,
  onSetErrorAlertOpen,
  onSetErrorButtonText,
  onSetErrorAction,
  onRetry,
}: ErrorHandlerOptions) {
  // Handle Firebase auth popup closed error
  if (error instanceof FirebaseError && error?.code === 'auth/popup-closed-by-user') {
    onSetErrorMessage?.(i18next.t('app.errors.signInCancelled'));
    onSetErrorAlertOpen?.(true);
    if (onSetErrorButtonText) {
      onSetErrorButtonText(i18next.t('app.buttons.retry'));
    }
    if (onSetErrorAction && onRetry) {
      onSetErrorAction(onRetry);
    }
    return;
  }

  // Handle bad url error
  if (
    error instanceof ApiError &&
    error.status === STATUS_CODES.NOT_FOUND &&
    error?.message?.toLowerCase().includes('valid') &&
    error?.message?.toLowerCase().includes('url')
  ) {
    onSetErrorMessage?.(i18next.t('app.errors.URLOrPromptRequired'));
    onSetErrorAlertOpen?.(true);
    return;
  }

  // Handle cant scrape website error
  if (error instanceof ApiError && error.status === STATUS_CODES.CANT_SCRAPE_WEBSITE) {
    onSetErrorMessage?.(i18next.t('app.errors.cantScrapeWebsite'));
    onSetErrorAlertOpen?.(true);
    return;
  }

  // Handle other errors
  const errorMessage =
    error instanceof Error ? error.message : i18next.t('app.errors.unexpectedError');
  onSetErrorMessage?.(errorMessage);
  onSetErrorAlertOpen?.(true);
  if (onSetErrorButtonText) {
    onSetErrorButtonText(i18next.t('app.buttons.retry'));
  }
  if (onSetErrorAction && onRetry) {
    onSetErrorAction(onRetry);
  }
}

/**
 * Get stored UTM data from localStorage for analytics
 */
function getStoredUTMData() {
  try {
    const storedAttribution = localStorage.getItem('signup_attribution');
    if (!storedAttribution) {
      return null;
    }
    return JSON.parse(storedAttribution);
  } catch (error) {
    console.warn('[App | Analytics] Error parsing stored UTM data:', error);
    return null;
  }
}

export async function register(userId: string) {
  try {
    // Get stored UTM data for analytics
    const utmData = getStoredUTMData();

    const url = `${getDirectUrl('register')}?user_id=${userId}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        utm_data: utmData,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(errorData, response.status);
    }
    return await response.json();
  } catch (error) {
    trackSentryError('Failed to register user', { userId, error });
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
  }
}

export async function redeemAppsumoCode(userId: string | undefined, code: string) {
  try {
    const url = `${getDirectUrl('redeem_code')}?user_id=${userId}&code=${code}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {},
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        i18next.t('app.errors.failedToRedeemCode')
      );
    }
    const jsonPayload = await response.json();
    return jsonPayload.data;
  } catch (error) {
    trackSentryError('Failed to redeem AppSumo code', { userId, code, error });
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
  }
}

export async function createNewCampaign(
  userId: string,
  url: string,
  prompt: string,
  creativity: Creativity,
  outputLanguage: OutputLanguageData,
  session: string
): Promise<string> {
  try {
    if (!url?.length && !prompt?.length) {
      throw new Error(i18next.t('app.errors.URLOrPromptRequired'));
    }

    const response = await fetch(getDirectUrl('new_campaign'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        url: url,
        prompt: prompt,
        creativity: creativity,
        language: outputLanguage.code,
        session: session,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        i18next.t('app.errors.failedToCreateNewCampaign')
      );
    }

    const data = await response.json();
    return data.data.campaign.id;
  } catch (error) {
    trackSentryError('Failed to create new campaign', {
      userId,
      url,
      prompt,
      creativity,
      outputLanguage: outputLanguage.code,
      session,
      error,
    });
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
  }
}

export async function finalizeCampaignCreation(
  userId: string,
  campaignId: string,
  session: string
): Promise<string> {
  try {
    const response = await fetch(getDirectUrl('finalize_campaign_creation'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        campaign_id: campaignId,
        session: session,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        i18next.t('app.errors.failedToFinalizeCampaignCreation')
      );
    }

    const data = await response.json();
    return data.data.campaign.id;
  } catch (error) {
    trackSentryError('Failed to finalize campaign creation', {
      userId,
      campaignId,
      session,
      error,
    });
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
  }
}

export async function regenerateItem(
  userId: string,
  campaignId: string,
  itemId: string,
  itemVersion: number,
  creativity: Creativity,
  session: string,
  prompt: string
): Promise<{ new_version: Version; updated_item: Item }> {
  try {
    const response = await fetch(getDirectUrl('regenerate_item'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        campaign_id: campaignId,
        item_id: itemId,
        item_version: itemVersion.toString(),
        creativity: creativity,
        session: session,
        prompt: prompt,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        i18next.t('app.errors.failedToRegenerateItem')
      );
    }

    const data = await response.json();
    return {
      new_version: data.data.new_version,
      updated_item: data.data.updated_item,
    };
  } catch (error) {
    trackSentryError('Failed to regenerate item', {
      userId,
      campaignId,
      itemId,
      itemVersion,
      creativity,
      session,
      prompt,
      error,
    });
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
  }
}

export async function generateTab(
  userId: string,
  campaignId: string,
  session: string,
  items: RequestType[],
  remarks: string
): Promise<void> {
  try {
    const response = await fetch(getDirectUrl('generate_tab'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        campaign_id: campaignId,
        session: session,
        items: items,
        remarks: remarks,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        i18next.t('app.errors.failedToGenerateTab')
      );
    }
  } catch (error) {
    trackSentryError('Failed to generate tab', {
      userId,
      campaignId,
      session,
      items,
      remarks,
      error,
    });
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
  }
}

export async function invalidateCredits(
  userId: string,
  session: string
): Promise<void> {
  try {
    const response = await fetch(getDirectUrl('invalidate_credits'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        session: session,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        i18next.t('app.errors.failedToInvalidateCredits')
      );
    }
  } catch (error) {
    trackSentryError('Failed to invalidate credits', { userId, session, error });
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
  }
}

export async function regenerateItemStructured(
  userId: string,
  campaignId: string,
  parentItemId: string,
  itemId: string,
  session: string,
  prompt?: string,
  creativity?: Creativity
): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/regenerate_structured_item`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        campaign_id: campaignId,
        parent_item_id: parentItemId,
        structured_item_id: itemId,
        session: session,
        prompt: prompt,
        creativity: creativity,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        'Failed to process structured item'
      );
    }
  } catch (error) {
    trackSentryError('Failed to regenerate structured item', {
      userId,
      campaignId,
      parentItemId,
      itemId,
      session,
      prompt,
      creativity,
      error,
    });
    if (error instanceof ApiError) {
      throw error;
    } else {
      throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
    }
  }
}

export async function generateMoreIdeas(
  userId: string,
  campaignId: string,
  parentItemId: string,
  session: string,
  creativity: Creativity
): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/generate_more`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        campaign_id: campaignId,
        parent_item_id: parentItemId,
        session: session,
        creativity: creativity,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(errorData, response.status, 'Failed to generate more ideas');
    }
  } catch (error) {
    trackSentryError('Failed to generate more ideas', {
      userId,
      campaignId,
      parentItemId,
      session,
      creativity,
      error,
    });
    if (error instanceof ApiError) {
      throw error;
    } else {
      throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
    }
  }
}

export async function revealStructuredItems(
  userId: string,
  campaignId: string,
  parentItemId: string,
  itemIds: string[],
  session: string
): Promise<{
  revealed_item_ids: string[];
  skipped_item_ids: { id: string; reason: string }[];
  charged_credits: number;
} | void> {
  try {
    const response = await fetch(`${API_BASE_URL}/reveal_structured_items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        campaign_id: campaignId,
        parent_item_id: parentItemId,
        item_ids: itemIds,
        session: session,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiError(
        errorData,
        response.status,
        'Failed to reveal structured items'
      );
    }

    try {
      return await response.json();
    } catch (error) {
      trackSentryError('Failed to parse response', {
        userId,
        campaignId,
        parentItemId,
        itemIds,
        session,
        error,
      });
      console.error('Failed to parse response', error);
      return; // backend may return minimal body; ignore
    }
  } catch (error) {
    trackSentryError('Failed to reveal structured items', {
      userId,
      campaignId,
      parentItemId,
      itemIds,
      session,
      error,
    });
    if (error instanceof ApiError) {
      throw error;
    } else {
      throw new ApiError(i18next.t('app.errors.unexpectedError'), 0);
    }
  }
}
